import { Component, ViewEncapsulation, forwardRef, ChangeDetectorRef, inject, input, output, linkedSignal, signal, computed } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';


@Component({
    selector: 'app-input',
    templateUrl: './input.component.html',
    styleUrls: ['./input.component.scss'],
    encapsulation: ViewEncapsulation.ShadowDom,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => InputComponent),
            multi: true
        }
    ],
    imports: [FormsModule]
})
export class InputComponent implements ControlValueAccessor {
  private cdr = inject(ChangeDetectorRef);

  placeholder = input('');
  disabled = input(false);
  readonly = input(false);
  min = input(-Infinity);
  max = input(Infinity);
  step = input(1);
  icon = input<'search' | 'email' | 'lock' | 'user' | false>(false);
  value = input<string>('');
  type = input<'text' | 'textarea' | 'password' | 'email' | 'number' | 'tel' | 'url' | 'date'>('text');
  size = input<'sm' | 'md' | 'lg' | 'xs'>('md');
  button = input<{ label: string; variant?: string; size?: string; disabled?: boolean } | undefined>(undefined);
  fakeButton = input(false);
  autocomplete = input<string>('off');
  autoResize = input<{ enabled: boolean; maxHeight?: number; minHeight?: number }>({ enabled: false });
  gapAndButtonMargin = input(false);
  buttonClick = output<void>();

  /** Internal disabled state that can be toggled by CVA setDisabledState */
  _disabledByCva = signal(false);

  /** Effective disabled state: true if either the input signal or the CVA says disabled */
  isDisabled = computed(() => this.disabled() || this._disabledByCva());

  /**
   * Writable signal linked to the `value` input.
   * - Follows `value()` when the parent changes the binding
   * - Can be independently set by `writeValue()` (CVA) or user input
   * - Unlike effect+signal, linkedSignal does NOT overwrite CVA-set values
   */
  private _value = linkedSignal<string, string>({
    source: this.value,
    computation: (src) => src || ''
  });
  private onChange = (value: string) => {};
  private onTouched = () => {};

  // Password visibility toggle
  showPassword = false;

  get internalValue(): string {
    return this._value();
  }

  set internalValue(val: string) {
    if (val !== this._value()) {
      this._value.set(val);
      this.onChange(val);
      this.cdr.detectChanges();
    }
  }

  writeValue(value: string): void {
    this._value.set(value || '');
    this.cdr.detectChanges();
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._disabledByCva.set(isDisabled);
    this.cdr.detectChanges();
  }

  onInput(e: Event): void {
    const target = e.target as HTMLInputElement;
    this.internalValue = target.value;
  }

  onBlur(): void {
    this.onTouched();
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  get actualInputType(): string {
    if (this.type() === 'password') {
      return this.showPassword ? 'text' : 'password';
    }
    return this.type();
  }
}
